import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Sparkles, Eye, EyeOff, Zap, MessageSquare, Shield, Globe, KeyRound, ArrowLeft } from "lucide-react";
import api from "../lib/api";
import SocialLoginButtons from "../components/SocialLoginButtons";

export default function LoginPage() {
  const { login, completeTwoFactor } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // 2FA challenge step (shown when login returns twofa_required).
  const [challengeToken, setChallengeToken] = useState(null);
  const [twofaCode, setTwofaCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const codeInputRef = useRef(null);

  useEffect(() => {
    if (challengeToken) codeInputRef.current?.focus();
  }, [challengeToken, useRecovery]);

  const finishLogin = async () => {
    toast.success("Welcome back!");
    try {
      const profileRes = await api.get("/profile/me");
      navigate(profileRes.data.has_profile ? "/feed" : "/profile/setup");
    } catch { navigate("/feed"); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.twofa_required) {
        // Password OK — swap the form for the verification-code step.
        setChallengeToken(result.challenge_token);
        setTwofaCode("");
        setUseRecovery(false);
        return;
      }
      if (!result?.success) {
        const msg = result?.error || "Login failed";
        setError(msg);
        toast.error(msg);
        return;
      }
      await finishLogin();
    } finally {
      setLoading(false);
    }
  };

  const handleTwofaSubmit = async (e) => {
    e.preventDefault();
    const code = twofaCode.trim();
    if (!code) return;
    setError("");
    setLoading(true);
    try {
      const result = await completeTwoFactor(challengeToken, code);
      if (!result?.success) {
        const msg = result?.error || "Verification failed";
        setError(msg);
        toast.error(msg);
        if (result?.status === 429) {
          // Locked out — send the user back to the credentials step.
          setChallengeToken(null);
          setTwofaCode("");
        }
        return;
      }
      await finishLogin();
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (value) => {
    // TOTP codes are 6 digits; recovery codes are alphanumeric.
    setTwofaCode(useRecovery ? value.trim() : value.replace(/\D/g, "").slice(0, 6));
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Branding Panel — desktop only */}
      <div className="hidden lg:flex lg:w-[45%] relative overflow-hidden flex-col justify-between p-12">
        <div className="absolute inset-0">
          <img src="https://static.prod-images.emergentagent.com/jobs/69522626-12bf-4c16-9d63-ce7a21ed458c/images/4b0c35a61aa330369e745c83398c904da64e29b6c0d63ad14d328b21f7468a34.jpeg" alt="" className="w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#05070f]/95 via-[#140d33]/85 to-[#0a1130]/90" />
          <div className="absolute inset-0 bg-aurora animate-aurora" />
        </div>
        <div className="absolute top-20 right-16 w-32 h-32 bg-white/5 rounded-3xl rotate-12 animate-float" />
        <div className="absolute bottom-32 left-16 w-20 h-20 bg-white/5 rounded-2xl -rotate-6 animate-float" style={{animationDelay: "2s"}} />

        <div className="relative z-10">
          <div className="flex items-center gap-2.5 mb-12">
            <div className="w-9 h-9 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Sparkles className="w-4.5 h-4.5 text-white" />
            </div>
            <span className="font-outfit text-xl font-semibold text-white">App-Mi.ai</span>
          </div>
          <h2 className="font-outfit text-3xl lg:text-4xl font-light text-white leading-tight mb-4">
            Your AI,<br /><span className="font-medium">Your Way</span>
          </h2>
          <p className="text-base text-cyan-200 leading-relaxed max-w-md">
            Create personalized AI assistants tailored to your unique needs. Powered by 8 free AI models.
          </p>
        </div>

        <div className="relative z-10 space-y-4">
          {[
            { icon: Zap, label: "8 free AI models from leading labs" },
            { icon: MessageSquare, label: "Voice & text chat with custom personalities" },
            { icon: Shield, label: "Secure, private conversations" },
            { icon: Globe, label: "Your own custom AI URL" },
          ].map((f, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                <f.icon className="w-4 h-4 text-cyan-200" />
              </div>
              <span className="text-sm text-cyan-100">{f.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Form Panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-16 relative bg-aurora">
        <div className="w-full max-w-md animate-fade-in-up">
          <div className="text-center mb-8 lg:text-left">
            <div className="w-14 h-14 mx-auto lg:mx-0 mb-4 rounded-2xl bg-gradient-to-br from-cyan-400 to-violet-500 flex items-center justify-center shadow-glow-sm">
              <Sparkles className="w-7 h-7 text-white" />
            </div>
            <h1 className="font-outfit text-2xl font-semibold text-foreground" data-testid="login-heading">Welcome back</h1>
            <p className="text-sm text-muted-foreground mt-1">Sign in to your App-Mi.ai account</p>
          </div>

          {challengeToken ? (
          <div className="glass-panel rounded-2xl shadow-card-dark p-8" data-testid="twofa-step">
            <div className="flex items-center gap-2 mb-1">
              <KeyRound className="w-4 h-4 text-cyan-400" />
              <h2 className="font-outfit text-base font-semibold text-foreground">Two-factor verification</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-5">
              {useRecovery ? "Enter one of your recovery codes." : "Enter the 6-digit code from your authenticator app."}
            </p>
            <form onSubmit={handleTwofaSubmit} className="space-y-5">
              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 text-sm text-red-400 animate-fade-in" data-testid="twofa-error">{error}</div>
              )}
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-1.5">{useRecovery ? "Recovery code" : "Verification code"}</label>
                <input
                  ref={codeInputRef}
                  data-testid="twofa-code-input"
                  type="text"
                  autoComplete="one-time-code"
                  inputMode={useRecovery ? "text" : "numeric"}
                  pattern={useRecovery ? undefined : "[0-9]*"}
                  maxLength={useRecovery ? 64 : 6}
                  value={twofaCode}
                  onChange={(e) => handleCodeChange(e.target.value)}
                  onPaste={(e) => { e.preventDefault(); handleCodeChange(e.clipboardData.getData("text")); }}
                  placeholder={useRecovery ? "Enter recovery code" : "000000"}
                  required
                  className={`w-full px-4 py-2.5 bg-muted/40 border border-border rounded-lg text-foreground placeholder-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-cyan-400/25 focus:border-cyan-400 transition-colors ${useRecovery ? "text-sm font-mono" : "text-xl tracking-[0.5em] text-center font-mono"}`}
                />
              </div>
              <Button data-testid="twofa-submit-btn" type="submit" disabled={loading || !twofaCode.trim()} className="w-full bg-gradient-to-r from-cyan-400 to-violet-500 hover:from-cyan-400 hover:to-violet-600 text-white h-11 rounded-lg text-sm font-medium shadow-glow-sm hover:-translate-y-0.5 transition-all press-down">
                {loading ? "Verifying..." : "Verify"}
              </Button>
            </form>
            <button
              type="button"
              onClick={() => { setUseRecovery(!useRecovery); setTwofaCode(""); setError(""); }}
              className="w-full text-center text-sm text-cyan-400 hover:text-cyan-300 font-medium bg-transparent border-0 cursor-pointer mt-4"
              data-testid="twofa-recovery-toggle"
            >
              {useRecovery ? "Use an authenticator code instead" : "Use a recovery code"}
            </button>
            <button
              type="button"
              onClick={() => { setChallengeToken(null); setTwofaCode(""); setError(""); setUseRecovery(false); }}
              className="w-full flex items-center justify-center gap-1 text-xs text-muted-foreground/70 hover:text-muted-foreground bg-transparent border-0 cursor-pointer mt-3"
              data-testid="twofa-back-btn"
            >
              <ArrowLeft className="w-3 h-3" /> Back to sign in
            </button>
          </div>
          ) : (
          <div className="glass-panel rounded-2xl shadow-card-dark p-8">
            {/* Social Login Buttons */}
            <SocialLoginButtons />
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-muted" />
              <span className="text-xs text-muted-foreground/70">or continue with email</span>
              <div className="flex-1 h-px bg-muted" />
            </div>
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 text-sm text-red-400 animate-fade-in" data-testid="login-error">{error}</div>
              )}
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-1.5">Email</label>
                <input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required className="w-full px-4 py-2.5 bg-muted/40 border border-border rounded-lg text-sm text-foreground placeholder-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-cyan-400/25 focus:border-cyan-400 transition-colors" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-1.5">Password</label>
                <div className="relative">
                  <input data-testid="login-password-input" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" required className="w-full px-4 py-2.5 bg-muted/40 border border-border rounded-lg text-sm text-foreground placeholder-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-cyan-400/25 focus:border-cyan-400 transition-colors pr-10" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/70 hover:text-muted-foreground bg-transparent border-0 cursor-pointer" data-testid="login-toggle-password">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button data-testid="login-submit-btn" type="submit" disabled={loading} className="w-full bg-gradient-to-r from-cyan-400 to-violet-500 hover:from-cyan-400 hover:to-violet-600 text-white h-11 rounded-lg text-sm font-medium shadow-glow-sm hover:-translate-y-0.5 transition-all press-down">
                {loading ? "Signing in..." : "Sign In"}
              </Button>
            </form>
            <p className="text-sm text-center text-muted-foreground mt-6">
              Don't have an account?{" "}
              <Link to="/register" className="text-cyan-400 hover:text-cyan-300 font-medium no-underline" data-testid="login-register-link">Create one</Link>
            </p>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
